import React, { useEffect, useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { apiService } from "../../services/api";

export interface ReportRawDataLoaderProps {
  /** When true, modal is visible and load query runs (if reportSettings + filterValues provided). */
  isOpen: boolean;
  onClose: () => void;
  /** Display name for the report (e.g. "Purchase register"). */
  reportName?: string;
  /** Report settings id for the API (e.g. "purchase_register"). */
  reportSettings?: string;
  /** Filter values keyed by BindValue (e.g. { txt_start_date: "2024-01-01", txt_end_date: "2024-01-31" }). */
  filterValues?: Record<string, any>;
  /** Optional logged-in user id for branch filtering. */
  loginedUserId?: string;
}

/**
 * Converts date from HTML input format (yyyy-MM-dd) to API format (dd-MM-yyyy).
 */
function formatDateForReportApi(value: string): string {
  if (!value || typeof value !== "string") return value;
  const trimmed = value.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${d}-${m}-${y}`;
  }
  return trimmed;
}

/**
 * Builds the request payload: ReportSettings + formatted filters + optional logined_userid.
 */
function buildReportDataPayload(
  reportSettings: string,
  filterValues: Record<string, any>,
  loginedUserId?: string
): Record<string, any> {
  const formattedFilters: Record<string, any> = {};
  for (const [key, value] of Object.entries(filterValues || {})) {
    if (value === null || value === undefined || value === "") continue;
    const str = String(value).trim();
    formattedFilters[key] = /^\d{4}-\d{2}-\d{2}$/.test(str)
      ? formatDateForReportApi(str)
      : str;
  }
  const payload: Record<string, any> = {
    ReportSettings: reportSettings,
    ...formattedFilters,
  };
  if (loginedUserId) {
    payload.logined_userid = loginedUserId;
  }
  return payload;
}

export const ReportRawDataLoader: React.FC<ReportRawDataLoaderProps> = ({
  isOpen,
  onClose,
  reportName = "Report",
  reportSettings,
  filterValues = {},
  loginedUserId,
}) => {
  const [rawData, setRawData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterColumn, setFilterColumn] = useState<string>("all");

  const loadQuery = useCallback(async () => {
    if (!reportSettings) return;
    setIsLoading(true);
    setRawData(null);
    try {
      const payload = buildReportDataPayload(
        reportSettings,
        filterValues,
        loginedUserId
      );
      const response = await apiService.request({
        endpoint: "/pabreport/report_data",
        method: "POST",
        params: { name: reportSettings },
        payload,
        includeAuthToken: true,
        responseType: "json",
      });
      if (response && typeof response === "object") {
        setRawData(response);
      } else {
        throw new Error("Unexpected response format from report_data endpoint");
      }
    } catch (error) {
      console.error("ReportRawDataLoader: failed to load", error);
      setRawData({
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch raw data",
      });
    } finally {
      setIsLoading(false);
    }
  }, [reportSettings, filterValues, loginedUserId]);

  // Run load query when modal opens with valid reportSettings
  useEffect(() => {
    if (isOpen && reportSettings) {
      loadQuery();
    }
  }, [isOpen, reportSettings, loadQuery]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRawData(null);
      setFilterText("");
    }
  }, [isOpen]);

  // Derive data and columns unconditionally (no hooks after this)
  const dataArray = rawData?.data || [];
  const totalCount = rawData?.totalcount || 0;
  const headerData = rawData?.header || [];
  const hasError =
    rawData?.error || (rawData && rawData.success === false);
  const columns =
    dataArray.length > 0 ? Object.keys(dataArray[0]) : [];

  // Front-end filter: must run unconditionally to keep hook order consistent
  const filteredData = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return dataArray;
    return dataArray.filter((row: Record<string, any>) => {
      if (filterColumn && filterColumn !== "all") {
        const val = row[filterColumn];
        const str =
          val !== null && val !== undefined ? String(val).toLowerCase() : "";
        return str.includes(q);
      }

      // Fallback: search all columns
      return columns.some((col) => {
        const val = row[col];
        const str =
          val !== null && val !== undefined ? String(val).toLowerCase() : "";
        return str.includes(q);
      });
    });
  }, [dataArray, columns, filterText, filterColumn]);

  if (!isOpen) return null;

  const handleDownloadJson = () => {
    const payload = {
      ...rawData,
      data: filteredData,
      totalcount: filteredData.length,
    };
    const jsonString = JSON.stringify(payload, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${reportName.replace(/\s+/g, "_")}_raw_data_${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filteredData, {
      header: columns,
      skipHeader: false,
    });
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    const filename = `${reportName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="fixed  inset-0 z-[999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-7xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Raw Data - {reportName}
            </h2>
            {totalCount > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {filterText.trim()
                  ? `Showing ${filteredData.length} of ${totalCount} records`
                  : `Total Records: ${totalCount}`}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Front-end filter */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter:
          </label>
          <select
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="w-48 lowercase px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          >
            <option value="all">All columns</option>
            {columns.map(col => (
              <option key={col} value={col}>
                {col.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder={filterColumn === "all" ? "Search in all columns..." : `Search in ${filterColumn.replace(/_/g, " ")}...`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
          />
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block w-8 h-8 border-4 border-gray-200 dark:border-gray-700 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  Loading raw data...
                </p>
              </div>
            </div>
          ) : hasError ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 dark:text-red-400 text-lg mb-2">
                  Error
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {rawData?.error || "Failed to load raw data"}
                </p>
              </div>
            </div>
          ) : dataArray.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No data available
                </p>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  No rows match the filter. Try a different search.
                </p>
              </div>
            </div>
          ) : (
            <>
              {headerData.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(headerData[0] || {}).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          {key.replace(/_/g, " ")}:{" "}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-auto px-6 py-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            {column.replace(/_/g, " ")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredData.map(
                        (row: Record<string, any>, index: number) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            {columns.map((column) => {
                              const value = row[column];
                              return (
                                <td
                                  key={column}
                                  className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap"
                                >
                                  {value !== null && value !== undefined
                                    ? String(value)
                                    : "-"}
                                </td>
                              );
                            })}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
          {dataArray.length > 0 && (
            <>
              <button
                onClick={handleDownloadExcel}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Excel
              </button>
              {/* <button
                onClick={handleDownloadJson}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download JSON
              </button> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
